import {
  EventRecordDto,
  MotherDto,
  VisitDataDto,
  VisitDto,
} from '@ecdlink/core';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import localForage from 'localforage';
import { addEventRecord } from '../eventRecord/eventRecord.actions';
import { addInfant } from '../infant/infant.actions';
import { setFulfilledThunkActionStatus, setThunkActionStatus } from '../utils';
import { v4 as uuidv4 } from 'uuid';
import {
  addAdditionalVisitForMother,
  addMother,
  getAllMotherEventRecordTypes,
  getMothers,
  getMotherVisits,
  updateMotherAddress,
  updateMotherContactDetails,
  getReferralsForMother,
  getCompletedReferralsForMother,
  updateMother,
  updateMotherDeliveryDate,
  getMotherStatusInfoForIds,
  getAllVisitsForMotherIds,
  syncMotherVisits,
  syncMotherEventRecords,
  upsertMothers,
} from './mother.actions';
import { MotherState, UpdateMotherDeliveryDateProps } from './mother.types';
import { Visit } from '@ecdlink/graphql';

const initialState: MotherState = {};

const motherSlice = createSlice({
  name: 'mother',
  initialState,
  reducers: {
    resetMotherState: (state) => {
      state.mothers = initialState.mothers;
    },

    addMother: (state, action: PayloadAction<MotherDto>) => {
      if (!state.mothers) state.mothers = [];
      state.mothers?.push(action.payload);
    },
    restartMotherVisit: (
      state,
      action: PayloadAction<{
        motherId: string;
        visitId: string;
      }>
    ) => {
      const { motherId, visitId } = action.payload;

      const mother = state.mothers?.find((x) => x.id === motherId);
      if (!mother) return; // Handle case where the mother is not found

      const visits = mother.visits || [];
      const now = new Date().toISOString();

      const existingVisitIndex = visits.findIndex((v) => v.id === visitId);
      if (existingVisitIndex === -1) return; // Handle case where the visit is not found

      const existingVisit = visits[existingVisitIndex];

      // Cancel current visit
      const updatedVisit = {
        ...existingVisit,
        isCancelled: true,
        synced: false,
      };

      // Create new visit
      const newVisit: VisitDto = {
        id: uuidv4(),
        dueDate: existingVisit.dueDate ?? null,
        actualVisitDate: existingVisit.actualVisitDate ?? null,
        attended: false,
        isCancelled: false,
        startedDate: '',
        plannedVisitDate: existingVisit.plannedVisitDate ?? null,
        orderDate: existingVisit.orderDate ?? null,
        visitType: existingVisit.visitType,
        insertedDate: now,
        clientId: existingVisit.clientId,
        linkedVisitId: existingVisit.id,
        synced: false,
        visitAnswers: [],
      };

      // Update visits array
      if (state.mothers) {
        state.mothers = state.mothers.map((m) =>
          m.id === motherId
            ? {
                ...m,
                visits: [
                  ...visits.slice(0, existingVisitIndex),
                  updatedVisit,
                  newVisit,
                  ...visits.slice(existingVisitIndex + 1),
                ],
              }
            : m
        );
      }
    },
    addMotherVisits: (state, action: PayloadAction<VisitDto[]>) => {
      action.payload.forEach((visit) => {
        const motherId = visit.clientId;
        if (!motherId) return;

        const mother = state.mothers?.find((m) => m.id === motherId);
        if (!mother) return;

        if (!mother.visits) {
          mother.visits = [];
        }

        const existingIndex = mother.visits.findIndex((v) => v.id === visit.id);

        const normalizedVisit = { ...visit, clientId: motherId };

        if (existingIndex === -1) {
          mother.visits.push(normalizedVisit);
        } else {
          mother.visits[existingIndex] = {
            ...mother.visits[existingIndex],
            ...visit,
            clientId: motherId,
          };
        }
      });
    },
    addMotherAdditionalVisit: (state, action: PayloadAction<VisitDto>) => {
      if (!state.mothers) return;

      const motherIndex = state.mothers.findIndex(
        (m) => m.id === action.payload.clientId
      );
      if (motherIndex === -1) return;

      const updatedMother = {
        ...state.mothers[motherIndex],
        visits: [
          ...(state.mothers[motherIndex].visits || []), // Spread existing visits or start with an empty array
          action.payload, // Add the new visit
        ],
      };

      // Update the state.mothers array immutably
      state.mothers = state.mothers.map((m, index) =>
        index === motherIndex ? updatedMother : m
      );
    },
    updateMomVisit: (
      state,
      action: PayloadAction<{
        motherId: string;
        visitId: string;
        visitAnswers: VisitDataDto[];
        visitName: string;
        isOnline: boolean;
      }>
    ) => {
      const { motherId, visitId, visitAnswers, visitName, isOnline } =
        action.payload;

      if (!state.mothers) return;

      const motherIndex = state.mothers.findIndex((m) => m.id === motherId);
      if (motherIndex === -1) return;

      const mother = state.mothers[motherIndex];
      if (!mother.visits) return;

      const visitIndex = mother.visits.findIndex((v) => v.id === visitId);
      if (visitIndex === -1) return;

      const visit = mother.visits[visitIndex];
      const existingAnswers = visit.visitAnswers ?? [];

      const now = new Date().toISOString();
      const isFollowUp = visitName === 'Follow up';

      // Build answer map for merging
      const answerMap = new Map<string, VisitDataDto>();

      existingAnswers.forEach((ans) => {
        const key = `${ans.visitName}|${ans.visitSection}|${ans.question}`;
        answerMap.set(key, ans);
      });

      visitAnswers.forEach((newAns) => {
        const key = `${newAns.visitName}|${newAns.visitSection}|${newAns.question}`;
        const existing = answerMap.get(key);
        answerMap.set(key, { ...(existing ?? {}), ...newAns });
      });

      mother.visits[visitIndex] = {
        ...visit,
        visitAnswers: Array.from(answerMap.values()),
        startedDate:
          !visit.startedDate || visit.startedDate === ''
            ? now
            : visit.startedDate,
        attended: isFollowUp ? true : visit.attended,
        actualVisitDate: isFollowUp ? now : visit.actualVisitDate,
        synced: isOnline,
      };
    },
    updateMother: (state, action: PayloadAction<MotherDto>) => {
      if (!state.mothers) return;

      const index = state.mothers.findIndex(
        (mother) => mother.id === action.payload.id
      );
      if (index === -1) return;

      // Only update statusInfo — ignore all other fields from payload
      state.mothers[index].statusInfo = action.payload.statusInfo;
    },
    addEventRecordToMother: (state, action: PayloadAction<EventRecordDto>) => {
      if (state.mothers) {
        for (let i = 0; i < state.mothers.length; i++) {
          if (state.mothers[i].id === action.payload.motherId) {
            state.mothers[i] = {
              ...state.mothers[i],
              eventRecord: action.payload,
              isActive: false, // archive the user,
            };
          }
        }
      }
    },
    updateMotherDeliveryDate: (
      state,
      action: PayloadAction<UpdateMotherDeliveryDateProps>
    ) => {
      if (state.mothers) {
        for (let i = 0; i < state.mothers.length; i++) {
          if (state.mothers[i].id === action.payload.id)
            state.mothers[i] = {
              ...state.mothers[i],
              expectedDateOfDelivery: action.payload.expectedDateOfDelivery,
              synced: false, // ensure it gets synced to backend
            };
        }
      }
    },
    setMotherTabClicked: (
      state,
      action: PayloadAction<{
        motherId: string;
        tab: 'visits' | 'progress' | 'referrals' | 'contact';
        clicked: boolean;
        isOnline: boolean;
      }>
    ) => {
      const { motherId, tab, clicked, isOnline } = action.payload;
      const mother = state.mothers?.find(
        (i) => i.id === motherId || i.userId === motherId
      );
      if (!mother) return;

      // Only act when we're being told the tab was actually viewed/clicked
      if (!clicked) return; // ← explicit: ignore "unclick" events

      let shouldMarkSynced = false;

      switch (tab) {
        case 'visits':
          if (!mother.clickedVisitTab) {
            mother.clickedVisitTab = true;
            shouldMarkSynced = true;
          }
          break;

        case 'progress':
          if (!mother.clickedProgressTab) {
            mother.clickedProgressTab = true;
            shouldMarkSynced = true;
          }
          break;

        case 'referrals':
          if (!mother.clickedReferralsTab) {
            mother.clickedReferralsTab = true;
            shouldMarkSynced = true;
          }
          break;

        case 'contact':
          if (!mother.clickedContactTab) {
            mother.clickedContactTab = true;
            shouldMarkSynced = true;
          }
          break;
      }

      if (shouldMarkSynced) {
        mother.synced = isOnline;
      }
    },
  },
  extraReducers: (builder) => {
    setThunkActionStatus(builder, addMother);
    setThunkActionStatus(builder, getMothers);
    setThunkActionStatus(builder, getMotherStatusInfoForIds);
    setThunkActionStatus(builder, updateMotherAddress);
    setThunkActionStatus(builder, updateMotherContactDetails);
    setThunkActionStatus(builder, getMotherVisits);
    setThunkActionStatus(builder, addAdditionalVisitForMother);
    setThunkActionStatus(builder, getReferralsForMother);
    setThunkActionStatus(builder, getCompletedReferralsForMother);
    setThunkActionStatus(builder, syncMotherVisits);
    setThunkActionStatus(builder, syncMotherEventRecords);
    setThunkActionStatus(builder, upsertMothers);
    builder.addCase(addMother.fulfilled, (state, action) => {
      if (state.mothers) {
        for (let i = 0; i < state.mothers.length; i++) {
          if (
            'synced' in state.mothers[i] &&
            state.mothers[i].synced === false
          ) {
            state.mothers[i] = {
              ...state.mothers[i],
              synced: true,
              isNew: false,
            };
          }
        }
      }
      setFulfilledThunkActionStatus(state, action);
    });
    builder.addCase(upsertMothers.fulfilled, (state, action) => {
      if (state.mothers) {
        for (let i = 0; i < state.mothers.length; i++) {
          if (
            'synced' in state.mothers[i] &&
            state.mothers[i].synced === false
          ) {
            state.mothers[i] = {
              ...state.mothers[i],
              synced: true,
              isNew: false,
            };
          }
        }
      }
      setFulfilledThunkActionStatus(state, action);
    });
    builder.addCase(getMothers.fulfilled, (state, action) => {
      const mothers = action.payload.map((mother) => ({
        ...mother,
        isActive: mother.isActive ?? true,
      })) as MotherDto[];

      state.mothers = mothers;
      setFulfilledThunkActionStatus(state, action);
    });
    builder.addCase(syncMotherVisits.fulfilled, (state, action) => {
      if (!state.mothers) return;

      state.mothers = state.mothers.map((mother) => ({
        ...mother,
        visits: Array.isArray(mother.visits)
          ? mother.visits.map((visit) => ({
              ...visit,
              synced: visit.synced === false ? true : visit.synced,
            }))
          : mother.visits,
      }));
      setFulfilledThunkActionStatus(state, action);
    });
    builder.addCase(syncMotherEventRecords.fulfilled, (state, action) => {
      if (!state.mothers) return;
      state.mothers = state.mothers.map((mother) => ({
        ...mother,
        eventRecord: {
          ...mother.eventRecord,
          synced: true,
        },
      }));
      setFulfilledThunkActionStatus(state, action);
    });
    builder.addCase(getMotherVisits.fulfilled, (state, action) => {
      const motherId = action.meta.arg.motherId;

      // Ensure mothers array exists
      if (!state.mothers) {
        state.mothers = [];
      }

      const mother = state.mothers?.find((m) => m.id === motherId);
      if (!mother) {
        // Optionally handle the case where mother isn't found
        setFulfilledThunkActionStatus(state, action);
        return;
      }

      // Start with existing visits (or empty array)
      const existingVisits = mother.visits || [];

      // Create a map of existing visits for O(1) lookup and update
      const visitMap = new Map(existingVisits.map((v) => [v.id, v]));

      // Merge/update with incoming visits
      action.payload.forEach((visit) => {
        visitMap.set(visit.id, {
          ...visitMap.get(visit.id), // existing fields if any
          ...visit, // new/overwriting fields
          clientId: motherId, // ensure clientId is set
          insertedDate: visit.insertedDate.toString(),
        });
      });

      // Assign the merged visits back (Immer allows this mutation)
      mother.visits = Array.from(visitMap.values());

      setFulfilledThunkActionStatus(state, action);
    });
    builder.addCase(getAllVisitsForMotherIds.fulfilled, (state, action) => {
      // Create a map: motherId → mother object (from actual state)
      const motherMap = new Map(
        (state.mothers || []).map((mother) => [mother.id, mother])
      );

      // Process each visit from the payload
      action.payload.forEach((visit) => {
        const motherId = visit.clientId;
        const mother = motherMap.get(motherId);

        if (!mother) {
          // Mother not in state yet — skip or handle differently if needed
          return;
        }

        // Ensure visits array exists
        if (!mother.visits) {
          mother.visits = [];
        }

        // Find index of existing visit with same id
        const existingIndex = mother.visits.findIndex((v) => v.id === visit.id);

        const visitWithClientId = { ...visit, clientId: motherId };

        if (existingIndex === -1) {
          // Add new visit
          mother.visits.push(visitWithClientId);
        } else {
          // Update existing visit (merge changes)
          mother.visits[existingIndex] = {
            ...mother.visits[existingIndex],
            ...visit,
            clientId: motherId, // ensure it's preserved
          };
        }
      });

      setFulfilledThunkActionStatus(state, action);
    });

    builder.addCase(getAllMotherEventRecordTypes.fulfilled, (state, action) => {
      state.eventRecordTypes = action.payload;
    });
    builder.addCase(addEventRecord.fulfilled, (state, action) => {
      const motherId = action.payload?.input?.motherId;

      if (motherId && action.payload.isCloseFolder) {
        state.mothers = state.mothers?.filter(
          (item) => item.user?.id !== motherId
        );
      }
    });
    builder.addCase(addInfant.fulfilled, (_state, _action) => {
      // Mother archival is driven by the baby_was_born event record, not by infant
      // registration. Removing the mother here was premature — the backend only
      // archives her when the event is explicitly logged (with an infant linked).
    });
    builder.addCase(getReferralsForMother.fulfilled, (state, action) => {
      state.referralsForMother = action.payload;
      setFulfilledThunkActionStatus(state, action);
    });
    builder.addCase(
      getCompletedReferralsForMother.fulfilled,
      (state, action) => {
        state.completedReferralsForMother = action.payload;
        setFulfilledThunkActionStatus(state, action);
      }
    );
    builder.addCase(addAdditionalVisitForMother.fulfilled, (state, action) => {
      const visit = action.payload;
      const motherId = (action.payload as Visit).motherId;

      if (!motherId) {
        setFulfilledThunkActionStatus(state, action);
        return;
      }

      const mother = state.mothers?.find((m) => m.id === motherId);
      if (!mother) {
        setFulfilledThunkActionStatus(state, action);
        return;
      }

      // Ensure visits array exists
      if (!mother.visits) {
        mother.visits = [];
      }

      // Check if visit already exists
      const existingIndex = mother.visits.findIndex((v) => v.id === visit.id);

      const visitWithClientId = { ...(visit as VisitDto), clientId: motherId };

      if (existingIndex === -1) {
        // New visit → append to end
        mother.visits.push(visitWithClientId);
      } else {
        // Existing → update in place (preserves order)
        mother.visits[existingIndex] = {
          ...mother.visits[existingIndex],
          ...visit,
          clientId: motherId,
        };
      }

      setFulfilledThunkActionStatus(state, action);
    });
    builder.addCase(updateMother.fulfilled, (state, action) => {
      if (!action.payload || !state.mothers) return;

      state.mothers = state.mothers?.map((item) => {
        if (item?.user?.id === action.payload?.user?.id) {
          // First, merge the item with the payload
          const mergedItem = { ...item, ...action.payload };

          // Now, handle the 'synced' property: set to true only if the original item's synced is false
          const finalItem = {
            ...mergedItem,
            synced: item.synced === false ? true : mergedItem.synced,
            isNew: false,
          };

          return finalItem;
        }
        return item; // Return the original item if no match
      });
      setFulfilledThunkActionStatus(state, action);
    });
    builder.addCase(updateMotherDeliveryDate.fulfilled, (state, action) => {
      if (!action.payload || !state.mothers) return;

      state.mothers = state.mothers?.map((item) => {
        if (item?.id === action.payload?.id) {
          return {
            ...item,
            expectedDateOfDelivery: action.payload.expectedDateOfDelivery,
          };
        }
        return item;
      });
      setFulfilledThunkActionStatus(state, action);
    });
    builder.addCase(getMotherStatusInfoForIds.fulfilled, (state, action) => {
      if (!action.payload || !state.mothers) return;

      const updates = action.payload;

      state.mothers = state.mothers.map((m) => {
        const update = updates.find((u) => u.userId === m.userId);
        return {
          ...m,
          statusInfo: update?.statusInfo ?? m.statusInfo,
          visitInfo: update?.visitInfo ?? m.visitInfo,
        };
      });
      setFulfilledThunkActionStatus(state, action);
    });
  },
});

const { reducer: motherReducer, actions: motherActions } = motherSlice;

const motherPersistConfig = {
  key: 'mother',
  storage: localForage,
  blacklist: [],
};

export { motherPersistConfig, motherReducer, motherActions };
